import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight, Mail, Linkedin, Share2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { PageTip } from "@/components/FieldHelp";
import api from "@/lib/api";

const TYPE_META = {
  email: { icon: Mail, cls: "border-l-amber-500 bg-amber-500/5" },
  linkedin: { icon: Linkedin, cls: "border-l-sky-500 bg-sky-500/5" },
  social: { icon: Share2, cls: "border-l-emerald-500 bg-emerald-500/5" },
  reminder: { icon: Mail, cls: "border-l-sky-500 bg-sky-500/5" },
};

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}

export default function Calendar() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const start = new Date(weekStart);
    const end = new Date(weekStart); end.setDate(end.getDate() + 7);
    const { data } = await api.get("/calendar", { params: { start: start.toISOString(), end: end.toISOString() } });
    setItems(data.items || []);
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const itemsFor = (d) => items.filter((it) => { const at = new Date(it.at); return at.getFullYear() === d.getFullYear() && at.getMonth() === d.getMonth() && at.getDate() === d.getDate(); });
  const label = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <PageBody>
      <PageHeader eyebrow="Calendar" title="Your week at a glance"
        subtitle="Scheduled email sends, LinkedIn task due dates, and social post dues - all in one week view. Click any item to open it." />
      <PageTip id="calendar">Times shown in your workspace timezone. Nothing here sends automatically except approved-and-scheduled emails when live sending is on.</PageTip>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-md" onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(n.getDate() - 7); return n; })} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" className="rounded-md" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="cal-today">This week</Button>
          <Button size="sm" variant="outline" className="rounded-md" onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(n.getDate() + 7); return n; })} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <span className="text-sm font-medium" data-testid="cal-range">{label}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="calendar-grid">
        {days.map((d, i) => {
          const isToday = d.getTime() === today.getTime();
          const dayItems = itemsFor(d);
          return (
            <div key={d.toISOString()} className={`rounded-xl border bg-card/40 p-3 min-h-[140px] ${isToday ? "border-amber-500/60" : "border-border/60"}`} data-testid={`cal-day-${i}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">{d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}</div>
              <div className="space-y-1.5">
                {dayItems.length === 0 ? <div className="text-[11px] text-muted-foreground/50">—</div> : dayItems.map((it) => {
                  const M = TYPE_META[it.type] || TYPE_META.email; const Icon = M.icon;
                  return (
                    <button key={it.id} onClick={() => navigate(it.link)} data-testid={`cal-item-${it.id}`}
                      className={`w-full text-left rounded-md border-l-2 ${M.cls} px-2 py-1.5 hover:opacity-80 transition-opacity`}>
                      <div className="flex items-center gap-1.5"><Icon className="h-3 w-3 shrink-0" /><span className="text-[11px] font-medium truncate">{it.title}</span></div>
                      <div className="text-[10px] text-muted-foreground truncate">{it.at_display}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </PageBody>
  );
}
